import { ColorThreshold } from 'src/components/TableV2/cells/ConditionalCell/ConditionalCell.types';

export function getColorForValue(
  value: number,
  thresholds: ColorThreshold[]
): { backgroundColor: string; color: string } {
  for (const threshold of thresholds) {
    const {
      min = -Infinity,
      max = Infinity,
      backgroundColor,
      color,
    } = threshold;
    if (value >= min && value < max) {
      return { backgroundColor, color };
    }
  }
  return { backgroundColor: 'transparent', color: 'inherit' };
}

// Creates color thresholds for a numeric range with 4 shades of red for negative values
// and 4 shades of green for positive values
export function createNumericColorGradientThresholds(
  values: number[]
): ColorThreshold[] {
  if (values.length === 0) return [];

  const negativeValues = values.filter((v) => v < 0);
  const positiveValues = values.filter((v) => v > 0);

  const thresholds: ColorThreshold[] = [];

  // Red gradient for negative values (4 shades)
  if (negativeValues.length > 0) {
    const sortedNegatives = [...negativeValues].toSorted((a, b) => a - b);
    const p25 = sortedNegatives[Math.floor(sortedNegatives.length * 0.25)];
    const p50 = sortedNegatives[Math.floor(sortedNegatives.length * 0.5)];
    const p75 = sortedNegatives[Math.floor(sortedNegatives.length * 0.75)];

    thresholds.push(
      { min: p75, max: 0, backgroundColor: '#FFCDD2', color: '#B71C1C' }, // Lightest red
      { min: p50, max: p75, backgroundColor: '#F44336', color: '#FFFFFF' },
      { min: p25, max: p50, backgroundColor: '#D32F2F', color: '#FFFFFF' },
      { max: p25, backgroundColor: '#B71C1C', color: '#FFFFFF' } // Darkest red for worst 25%
    );
  }

  // Green gradient for positive values (4 shades)
  if (positiveValues.length > 0) {
    const sortedPositives = [...positiveValues].toSorted((a, b) => a - b);
    const p25 = sortedPositives[Math.floor(sortedPositives.length * 0.25)];
    const p50 = sortedPositives[Math.floor(sortedPositives.length * 0.5)];
    const p75 = sortedPositives[Math.floor(sortedPositives.length * 0.75)];

    thresholds.push(
      { min: p75, backgroundColor: '#105615', color: '#FFFFFF' }, // Darkest green for top 25%
      { min: p50, max: p75, backgroundColor: '#34CB3D', color: '#FFFFFF' },
      { min: p25, max: p50, backgroundColor: '#AEEAB2', color: '#006B2C' },
      { min: 0, max: p25, backgroundColor: '#D4F7D6', color: '#006B2C' } // Lightest green
    );
  }

  // Handle zero if it exists in the data
  if (values.includes(0)) {
    thresholds.push({
      min: 0,
      max: 0,
      backgroundColor: '#F5F5F5',
      color: '#616161',
    });
  }

  return thresholds;
}
